import { DragDropModule } from "@angular/cdk/drag-drop";
import { ScrollingModule } from "@angular/cdk/scrolling";
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { FormArray, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatChipsModule } from "@angular/material/chips";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatDividerModule } from "@angular/material/divider";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatMenuModule } from "@angular/material/menu";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatSelectModule } from "@angular/material/select";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { MatTableModule } from "@angular/material/table";
import { MatTabsModule } from "@angular/material/tabs";
import { MatTooltipModule } from "@angular/material/tooltip";
import {
  TableVirtualScrollDataSource,
  TableVirtualScrollModule,
} from "ng-table-virtual-scroll";
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from "rxjs";

import { ImportColumnConfig, ImportConfig, ImportMetadata } from "../../interfaces/import-config.interface";
import { ImportErrorCollection } from "../../models/import-error.model";
import {
  ExcelProcessorService,
  ExcelSheet,
  ProcessedExcelFile,
} from "../../services/excel-processor.service";
import {
  ImportApiService,
  ImportProgress,
} from "../../services/import-api.service";
import { ImportValidationService } from "../../services/import-validation.service";
import { PreValidationService } from "../../services/pre-validation.service";
import { PreValidationResponse } from "../../interfaces/pre-validation.interface";
import { RowErrorsDialogComponent } from "../row-errors-dialog/row-errors-dialog.component";
import { HeaderValidationErrorDialogComponent } from "../header-validation-error-dialog/header-validation-error-dialog.component";
import { OrderAttributes } from "@albatrosdeveloper/ave-models-package";

interface ImportDialogData {
  config: ImportConfig;
}

interface ColumnFilter {
  columnKey: string;
  selectedValues: Set<string>;
  allValues: string[];
  filteredValues: string[];
  searchTerm: string;
}

interface TableFilters {
  globalSearch: string;
  showErrorsOnly: boolean;
  showValidOnly: boolean;
  showProcessedOnly: boolean;
  showPendingOnly: boolean;
  columnFilters: Map<string, ColumnFilter>;
}

interface ApiProcessError {
  message: string;
  type: string;
}

interface ApiInvalidColumn {
  headerName: string;
  key: string;
  errors: ApiProcessError[];
}

interface ApiInvalidRecord {
  index: number;
  columns: ApiInvalidColumn[];
}

interface ApiProcessResult {
  totalRecords: number;
  validRecords: number;
  invalidRecords: ApiInvalidRecord[];
  createdCount: number;
  errorCount: number;
}

export interface ApiResponseDataImport {
  processResult: ApiProcessResult;
  invalidRecords: ApiInvalidRecord[];
  totalInvalidRecords: number;
  message: string;
}

interface ApiResponse {
  message: string;
  data: ApiResponseDataImport;
  success?: boolean;
}

type RecordState = "pending" | "processed" | "error" | "validation_error";

type TableDataSource = {
  rowIndex: number;
  data: unknown;
  formGroup: FormGroup;
};

@Component({
  selector: "app-import-dialog",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatTabsModule,
    MatTableModule,
    MatIconModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatInputModule,
    MatFormFieldModule,
    MatMenuModule,
    MatSelectModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDividerModule,
    DragDropModule,
    ScrollingModule,
    TableVirtualScrollModule,
  ],
  templateUrl: "./import-dialog.component.html",
  styleUrls: ["./import-dialog.component.scss"],
})
export class ImportDialogComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  config: ImportConfig;
  processedFile: ProcessedExcelFile | null = null;
  selectedSheetIndex = 0;
  currentSheet: ExcelSheet | null = null;
  formArray: FormArray | null = null;
  errorCollection = new ImportErrorCollection();

  // UI State
  isFileSelected = false;
  isProcessing = false;
  isValidating = false;
  isImporting = false;
  dragOver = false;

  // Progress tracking
  progress: ImportProgress | null = null;

  // Pre-validation state
  isPreValidating = false;
  preValidationCompleted = false;
  preValidationFailed = false;
  preValidationErrorMessage = '';
  preValidatedRowCount = 0;
  skipPreValidation = false;
  currentPreValidationController: AbortController | null = null;

  // Table configuration
  displayedColumns: string[] = [];
  relatedColumnsMetadata = new Map<string, { displayName: string; dataType: string }>();
  dataSource: TableDataSource[] = [];
  filteredDataSource: TableDataSource[] = [];
  virtualTableDataSource = new TableVirtualScrollDataSource<TableDataSource>(
    []
  );

  // Performance optimization: Pre-computed values for template
  totalRecords = 0;
  filteredRecordsCount = 0;
  invalidRecordsCount = 0;
  validRecordsCount = 0;
  processedRecordsCount = 0;
  unprocessedValidRecordsCount = 0;
  activeFiltersCount = 0;
  totalErrorCount = 0;

  // Optimized performance caches for large datasets
  private rowErrorCountCache = new Map<number, number>();
  private cellErrorsCache = new Map<string, string[]>();
  private rowHasErrorsCache = new Map<number, boolean>();
  private columnFilterCache = new Map<string, boolean>();
  private cellTooltipCache = new Map<string, string>();
  private columnValuesCache = new Map<string, string[]>();

  // Memory-efficient data structures for 28k+ records
  private readonly visibleRowsSet = new Set<number>(); // Track visible rows for optimized rendering
  private readonly dirtyRowsSet = new Set<number>(); // Track rows that need re-validation
  private readonly errorRowsSet = new Set<number>(); // Fast lookup for rows with errors
  private columnIndexMap = new Map<string, number>(); // Fast column lookup
  private filterSearchDebounceMap = new Map<string, Subject<string>>();

  // High-performance optimizations for large datasets (28k+ records)
  private static readonly BATCH_SIZE = 500; // Process in batches of 500
  private static readonly VALIDATION_BATCH_SIZE = 1000; // Validate in batches of 1000
  private static readonly CACHE_CLEANUP_THRESHOLD = 10000; // Clean cache when over 10k items
  private static readonly VIRTUAL_BUFFER_MULTIPLIER = 0.2; // Smaller buffer for better memory
  private static readonly LARGE_DATASET_THRESHOLD = 5000; // Switch to optimized mode

  private validationQueue: Array<{ startIndex: number; endIndex: number }> = [];
  private isValidationInProgress = false;
  private processedValidationBatches = 0;
  private totalValidationBatches = 0;
  private isLargeDataset = false;

  // Debounced search
  private searchSubject = new Subject<string>();

  // Filter configuration
  filters: TableFilters = {
    globalSearch: "",
    showErrorsOnly: false,
    showValidOnly: false,
    showProcessedOnly: false,
    showPendingOnly: false,
    columnFilters: new Map(),
  };

  // Record state tracking
  recordStates: Map<number, RecordState> = new Map();
  processedRecordIndices: Set<number> = new Set();

  // File input
  selectedFile: File | null = null;

  // Virtual scrolling configuration
  virtualScrollItemSize = 50; // Height of each row in pixels
  headerHeight = 56; // Height of header row in pixels

  // Sorting configuration
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  // Column visibility management
  private emptyOptionalColumns = new Set<string>(); // Solo columnas opcionales vacías
  columnVisibilityMap = new Map<string, boolean>(); // Mapa de visibilidad por columna
  showColumnPanel = false; // Estado del panel lateral
  hiddenColumnsCount = 0; // Cache para performance

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ImportDialogData,
    private dialogRef: MatDialogRef<ImportDialogComponent>,
    private dialog: MatDialog,
    private excelProcessor: ExcelProcessorService,
    private validationService: ImportValidationService,
    private apiService: ImportApiService,
    private preValidationService: PreValidationService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {
    this.config = data.config;
    this.setupDisplayedColumns();
  }

  ngOnInit(): void {
    this.subscribeToProgress();
    this.setupDebouncedSearch();
    this.setGridColumnsCount();
    this.initializeDefaultSorting();
  }

  private setupDebouncedSearch(): void {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((searchTerm) => {
        this.filters.globalSearch = searchTerm;
        this.applyFilters();
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.apiService.clearProgress();

    // Clean up debounce subjects to prevent memory leaks
    this.filterSearchDebounceMap.forEach((subject) => {
      subject.complete();
    });
    this.filterSearchDebounceMap.clear();

    // Clear all caches to prevent memory leaks
    this.clearAllCaches();
    this.highlightCache.clear();
    this.highlightTextCache.clear();
  }

  private setupDisplayedColumns(): void {
    this.displayedColumns = [
      "rowNumber",
      ...this.config.columns.map((col) => col.key),
      "actions",
    ];
  }

  /**
   * Detecta columnas relacionadas que tienen datos y las agrega a displayedColumns
   * Se ejecuta después de pre-validación para mostrar columnas auto-completadas
   */
  private updateDisplayedColumnsWithRelatedData(): void {
    if (!this.formArray) return;

    // Obtener todas las columnas relacionadas configuradas
    const allRelatedColumns = new Set<string>();
    this.config.columns.forEach(col => {
      if (col.relatedColumns && col.relatedColumns.length > 0) {
        col.relatedColumns.forEach(relCol => allRelatedColumns.add(relCol));
      }
    });

    // Detectar qué columnas relacionadas tienen al menos un valor
    const columnsWithData = new Set<string>();
    this.formArray.controls.forEach(control => {
      const formGroup = control as FormGroup;
      allRelatedColumns.forEach(colKey => {
        const value = formGroup.get(colKey)?.value;
        if (value !== null && value !== undefined && value !== '') {
          columnsWithData.add(colKey);
        }
      });
    });

    // Obtener columnas base (ya definidas en config)
    const baseColumns = this.config.columns.map(col => col.key);

    // Agregar metadata SOLO para columnas que NO están en config.columns
    columnsWithData.forEach(colKey => {
      // Skip if already in base columns (prevent duplicates)
      if (baseColumns.includes(colKey)) {
        return;
      }

      // Add metadata only if not already present
      if (!this.relatedColumnsMetadata.has(colKey)) {
        this.relatedColumnsMetadata.set(colKey, {
          displayName: this.formatColumnName(colKey),
          dataType: 'string'
        });
      }

      // ✅ ALWAYS set visibility to true for related columns with data
      // This ensures they show automatically after pre-validation
      this.columnVisibilityMap.set(colKey, true);
    });

    // Rebuild displayedColumns using updateDisplayedColumns() to respect visibility settings
    this.updateDisplayedColumns();
  }

  /**
   * Formatea el nombre de una columna para display (convierte snake_case a Title Case)
   */
  private formatColumnName(key: string): string {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private subscribeToProgress(): void {
    this.apiService.progress$
      .pipe(takeUntil(this.destroy$))
      .subscribe((progress) => {
        this.progress = progress;
        this.isProcessing =
          progress?.stage === "processing" || progress?.stage === "uploading";
        this.isValidating = progress?.stage === "validating";
        this.isImporting =
          progress?.stage !== "completed" &&
          progress?.stage !== "error" &&
          progress !== null;
        this.cdr.markForCheck();
      });
  }

  // Method to calculate sticky column positioning
  getStickyColumnStyle(columnIndex: number, isHeader: boolean) {
    const column = this.config.columns[columnIndex];

    if (!column?.sticky) {
      return {};
    }

    // Calculate left position for sticky columns
    let leftPosition = 60; // Start after row number column (60px)

    // Add width of previous sticky columns
    for (let i = 0; i < columnIndex; i++) {
      if (this.config.columns[i].sticky) {
        leftPosition += 120; // Default column width
      }
    }

    return {
      left: `${leftPosition}px`,
    };
  }

  // Helper method to measure actual text width (not truncated)
  private measureTextWidth(text: string, font: string = '14px Roboto, sans-serif'): number {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return 0;
    context.font = font;
    return Math.ceil(context.measureText(text).width);
  }

  // Method to auto-resize table columns after data load
  private autoResizeTableColumns(): void {
    if (!this.dataSource || this.dataSource.length === 0 || !this.config?.columns) {
      return;
    }

    // Wait for DOM to update with increased timeout for stability
    setTimeout(() => {
      requestAnimationFrame(() => {
      const table = document.querySelector("table.import-dialog-container-table") as HTMLTableElement;
      if (!table) return;

      const headerCells = table.querySelectorAll("th[mat-header-cell]");
      if (headerCells.length === 0) return;

      // Map displayedColumns to their indices (skip rowNumber and actions)
      const dataColumnStartIndex = 1; // After rowNumber column

      // Helper function to resize a column
      const resizeColumn = (columnKey: string, displayName: string, isRequired: boolean = false) => {
        // Skip if column is not visible
        if (!this.isColumnVisible(columnKey)) return;

        // Find the actual header cell index in the DOM
        const displayedIndex = this.displayedColumns.indexOf(columnKey);
        if (displayedIndex === -1) return;

        const headerCell = headerCells[displayedIndex] as HTMLElement;
        if (!headerCell) return;

        // 1. Calculate header width using actual text measurement
        const headerText = displayName + (isRequired ? ' (*)' : '');
        const headerTextWidth = this.measureTextWidth(headerText, '600 14px Roboto, sans-serif');
        const headerPadding = 100; // Space for sort icon, filter button, padding
        const headerWidth = headerTextWidth + headerPadding;

        // 2. Calculate max content width from actual data (not truncated DOM)
        let maxContentWidth = 0;
        const sampleRate = this.dataSource.length <= 100 ? 1 : 5; // Sample every 5th row for large datasets

        for (let i = 0; i < this.dataSource.length; i += sampleRate) {
          const rowData = this.dataSource[i];
          const cellValue = this.getCellValue(rowData, columnKey);

          if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
            const cellText = String(cellValue);
            const textWidth = this.measureTextWidth(cellText, '400 14px Roboto, sans-serif');
            maxContentWidth = Math.max(maxContentWidth, textWidth);
          }
        }

        // Add padding for cell icons (check/error icons)
        const cellPadding = 80; // Space for icons + padding
        const contentWidth = maxContentWidth + cellPadding;

        // 3. Calculate final width: max of header and content
        const calculatedWidth = Math.max(headerWidth, contentWidth);

        // 4. Apply intelligent limits
        const absoluteMinWidth = 100;
        const minWidth = Math.max(headerWidth, absoluteMinWidth);
        const maxWidthLimit = 800;

        const finalWidth = Math.min(
          Math.max(calculatedWidth, minWidth),
          maxWidthLimit
        );

        // 5. Apply width to header and cells
        headerCell.style.width = `${finalWidth}px`;
        headerCell.style.minWidth = `${finalWidth}px`;

        // Apply to all cells in this column
        const rows = table.querySelectorAll("tr[mat-row]");
        rows.forEach((row) => {
          const cell = row.children[displayedIndex] as HTMLElement;
          if (cell) {
            cell.style.width = `${finalWidth}px`;
            cell.style.minWidth = `${finalWidth}px`;
          }
        });
      };

      // Process base config columns
      this.config.columns.forEach((column) => {
        resizeColumn(column.key, column.displayName, column.required);
      });

      // Process related columns (auto-populated from pre-validation)
      this.relatedColumnsMetadata.forEach((metadata, columnKey) => {
        resizeColumn(columnKey, metadata.displayName, false);
      });
      });
    }, 300);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.handleFile(input.files[0]);
    }
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = false;

    if (event.dataTransfer?.files && event.dataTransfer.files[0]) {
      this.handleFile(event.dataTransfer.files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver = true;
  }

  onDragLeave(): void {
    this.dragOver = false;
  }

  private async handleFile(file: File): Promise<void> {
    try {
      this.selectedFile = file;
      this.isProcessing = true;

      // Validate file requirements
      const fileValidation = this.validationService.validateFileRequirements(
        file,
        this.config
      );
      if (!fileValidation.isValid) {
        this.showErrors(fileValidation.errors);
        this.resetModalState();
        return;
      }

      // Validate file type
      if (!this.excelProcessor.validateFileType(file)) {
        this.showError(
          "Tipo de archivo no válido. Solo se aceptan archivos Excel (.xlsx, .xls)"
        );
        this.resetModalState();
        return;
      }

      // Validate row count (maximum 1000 rows)
      const maxRowsComponent = 5000;
      const rowCountValidation = await this.excelProcessor.validateRowCount(
        file,
        maxRowsComponent
      );
      if (!rowCountValidation.isValid) {
        this.showError(
          `El archivo excede el límite máximo de ${maxRowsComponent} filas. El archivo contiene ${rowCountValidation.actualRows} filas. Por favor, divida el archivo en partes más pequeñas.`
        );
        this.resetModalState();
        return;
      }

      // Validate Excel headers match expected template
      const headerValidation = await this.excelProcessor.validateExcelHeaders(
        file,
        this.config
      );
      if (!headerValidation.isValid) {
        // Open modal dialog instead of showing snackbar
        this.dialog.open(HeaderValidationErrorDialogComponent, {
          data: {
            missingColumns: headerValidation.missingColumns,
            warnings: headerValidation.warnings,
            columns: this.config.columns,
            configTitle: this.config.title
          },
          width: '95vw',
          maxWidth: '600px',
          height: '95vh',
          maxHeight: '600px',
          disableClose: false
        });
        this.resetModalState();
        return;
      }

      // Show warnings if any optional columns are missing
      if (headerValidation.warnings.length > 0) {
        headerValidation.warnings.forEach(warning => this.showWarning(warning));
      }

      // Process Excel file
      this.processedFile = await this.excelProcessor.processFile(
        file,
        this.config
      );
      this.isFileSelected = true;

      // Set default sheet and validate
      if (this.processedFile.sheets.length > 0) {
        this.selectedSheetIndex = 0;
        this.selectSheet(0);
      }

      this.showSuccess("Archivo cargado exitosamente");

      // Trigger automatic pre-validation if configured (FR-3)
      if (
        this.config.preValidation?.enabled &&
        this.config.preValidation.mode === 'automatic'
      ) {
        await this.executePreValidation();
      }
    } catch (error) {
      this.showError(
        `Error procesando archivo: ${error instanceof Error ? error.message : "Error desconocido"}`
      );
      this.resetModalState();
    } finally {
      this.isProcessing = false;
    }
  }

  selectSheet(index: number): void {
    if (!this.processedFile || !this.processedFile.sheets[index]) {
      return;
    }

    this.selectedSheetIndex = index;
    this.currentSheet = this.processedFile.sheets[index];

    // Create form array for the sheet data
    this.formArray = this.validationService.createFormArray(
      this.currentSheet.processedData,
      this.config
    );

    // Determine if this is a large dataset
    this.isLargeDataset =
      this.formArray.length >= ImportDialogComponent.LARGE_DATASET_THRESHOLD;

    // Update data source for table
    this.dataSource = this.formArray.controls.map((control, index) => ({
      rowIndex: index,
      formGroup: control as FormGroup,
      data: this.currentSheet!.processedData[index],
    }));

    // Analyze and hide empty optional columns
    this.analyzeEmptyColumns();

    // Build column index map for fast lookups
    this.buildColumnIndexMap();

    // Initialize filters and apply them
    this.initializeFilters();

    // Validate the sheet (with batching for large datasets)
    if (this.isLargeDataset) {
      this.validateCurrentSheetBatched();
    } else {
      this.validateCurrentSheet();
    }

    // Auto-resize table columns after data load
    this.updateAllCachedValues();
    this.autoResizeTableColumns();
    this.cdr.markForCheck();
  }

  private validateCurrentSheet(): void {
    if (!this.formArray) return;

    this.isValidating = true;
    this.errorCollection = this.validationService.updateFormArrayValidation(
      this.formArray,
      this.config
    );
    this.isValidating = false;

    const errorCount = this.errorCollection.getTotalErrors();
    if (errorCount > 0) {
      this.showWarning(`Se encontraron ${errorCount} errores de validación`);
    }

    this.updateAllCachedValues();
    this.cdr.markForCheck();
  }

  /**
   * Execute pre-validation handler
   * Calls backend to validate data and populate related columns before import
   */
  async executePreValidation(): Promise<void> {
    // Check if pre-validation is enabled
    if (!this.config.preValidation?.enabled) {
      return;
    }

    // Edge case: Check for required data
    if (!this.dataSource || this.dataSource.length === 0) {
      this.showWarning('No hay datos para pre-validar');
      return;
    }

    if (!this.selectedFile || !this.currentSheet) {
      this.showError('Error: Falta información del archivo');
      return;
    }

    if (!this.formArray) {
      this.showError('Error: Formulario no inicializado');
      return;
    }

    // Cancel previous pre-validation if running (FR-15)
    if (this.currentPreValidationController) {
      this.currentPreValidationController.abort();
    }

    // Create new AbortController
    this.currentPreValidationController = new AbortController();
    const signal = this.currentPreValidationController.signal;

    // Set loading state and reset previous state
    this.isPreValidating = true;
    this.preValidationFailed = false;
    this.preValidationCompleted = false;  // Reset para nueva validación
    this.preValidationErrorMessage = '';

    try {
      // Get ALL row data (including rows with frontend errors per FR-6)
      const data = this.dataSource.map((row) => {
        const cleanRow = { ...row.formGroup.value };
        // Remove internal control fields
        delete cleanRow._rowIndex;
        delete cleanRow._isValid;
        delete cleanRow._errors;
        delete cleanRow._isPreValidated;
        delete cleanRow._preValidationResult;
        return cleanRow;
      });

      // Create metadata
      const metadata: ImportMetadata = {
        fileName: this.selectedFile.name,
        sheetName: this.currentSheet.name,
        totalRows: data.length,
        columns: this.config.columns.map((col) => col.key),
        timestamp: new Date().toISOString(),
      };

      // Call pre-validation handler
      const result: PreValidationResponse =
        await this.preValidationService.executePreValidation(
          this.config.preValidation.handler,
          data,
          metadata,
          signal
        );

      // Map response to FormArray
      if (this.formArray) {
        this.preValidationService.mapResponseToFormArray(
          result,
          this.formArray,
          this.config
        );
      }

      // Update displayedColumns with related columns data
      this.updateDisplayedColumnsWithRelatedData();

      // Force change detection to ensure UI updates with new columns
      this.cdr.detectChanges();

      // Update state
      this.preValidationCompleted = true;
      this.preValidatedRowCount = result.validCount;

      // Update caches and stats
      this.updateAllCachedValues();
      this.applyFilters();

      // Call onPreValidationComplete callback
      if (this.config.preValidation.onPreValidationComplete) {
        this.config.preValidation.onPreValidationComplete(result);
      }

      // Show success message
      if (result.invalidCount > 0) {
        this.showWarning(
          `Pre-validación completada: ${result.validCount} válidas, ${result.invalidCount} con errores`
        );
      } else {
        this.showSuccess(
          `Pre-validación exitosa: ${result.validCount} filas listas para importar`
        );
      }
    } catch (error: any) {
      // Handle AbortError (user cancellation)
      if (error.name === 'AbortError') {
        return;
      }

      // Handle other errors
      this.preValidationFailed = true;
      this.preValidationErrorMessage = error.message || 'Error desconocido';

      // Mark as completed even on HTTP error to allow import (FR-18)
      // This prevents blocking the user from importing when the endpoint fails
      this.preValidationCompleted = true;

      // Call onPreValidationError callback
      if (this.config.preValidation.onPreValidationError) {
        this.config.preValidation.onPreValidationError(error);
      }

      // Show error notification
      this.showError(
        `La pre-validación falló: ${this.preValidationErrorMessage}. Puede continuar e importar sin pre-validar.`
      );
    } finally {
      this.isPreValidating = false;
      this.currentPreValidationController = null;
      this.cdr.markForCheck();
    }
  }

  /**
   * Retry pre-validation after a failure
   */
  retryPreValidation(): void {
    this.preValidationFailed = false;
    this.preValidationErrorMessage = '';
    this.executePreValidation();
  }

  // Advanced cache management for large datasets
  private clearAllCaches(): void {
    // Only clear caches if dataset is not large, or if we exceed memory threshold
    const shouldClearAll =
      !this.isLargeDataset ||
      this.rowErrorCountCache.size >
        ImportDialogComponent.CACHE_CLEANUP_THRESHOLD;

    if (shouldClearAll) {
      this.rowErrorCountCache.clear();
      this.cellErrorsCache.clear();
      this.rowHasErrorsCache.clear();
      this.columnFilterCache.clear();
      this.cellTooltipCache.clear();
    }

    // Always clear tracking sets as they're lightweight
    this.visibleRowsSet.clear();
    this.dirtyRowsSet.clear();
    this.errorRowsSet.clear();

    // Clear UI caches to prevent memory leaks
    this.highlightCache.clear();
    this.highlightTextCache.clear();

    // Don't clear columnValuesCache as it's expensive to rebuild
  }

  private clearColumnValuesCache(): void {
    this.columnValuesCache.clear();
    this.columnIndexMap.clear();
  }

  private updateAllCachedValues(): void {
    this.clearAllCaches();

    // Pre-compute all values that are used in templates
    this.totalRecords = this.dataSource.length;
    this.filteredRecordsCount = this.filteredDataSource.length;
    this.totalErrorCount = this.errorCollection.getTotalErrors();
    this.processedRecordsCount = this.processedRecordIndices.size;

    // Cache row-level computations
    this.dataSource.forEach((row) => {
      this.getCachedRowErrorCount(row);
      this.getCachedRowHasErrors(row);
    });

    // Cache column filter states
    this.config.columns.forEach((column) => {
      this.getCachedColumnFilterState(column.key);
    });

    // Update computed counts
    this.invalidRecordsCount = this.getInvalidRecords().length;
    this.validRecordsCount = this.getValidRecords().length;
    this.unprocessedValidRecordsCount =
      this.getUnprocessedValidRecords().length;
    this.activeFiltersCount = this.calculateActiveFiltersCount();
  }

  // Optimized getter methods that use cache
  getCachedRowErrorCount(rowData: TableDataSource): number {
    const cacheKey = rowData.rowIndex;
    if (this.rowErrorCountCache.has(cacheKey)) {
      return this.rowErrorCountCache.get(cacheKey)!;
    }

    let errorCount = 0;
    this.config.columns.forEach((column) => {
      const cellErrors = this.getCachedCellErrors(rowData, column.key);
      errorCount += cellErrors.length;
    });

    this.rowErrorCountCache.set(cacheKey, errorCount);
    return errorCount;
  }

  getCachedCellErrors(rowData: TableDataSource, columnKey: string): string[] {
    const cacheKey = `${rowData.rowIndex}-${columnKey}`;
    if (this.cellErrorsCache.has(cacheKey)) {
      return this.cellErrorsCache.get(cacheKey)!;
    }

    const control = rowData.formGroup.get(columnKey);
    const errors: string[] = [];

    if (control && control.errors) {
      Object.entries(control.errors).forEach(
        ([errorKey, errorValue]: [string, unknown]) => {
          if (errorKey.includes("apiError") && typeof errorValue === "string") {
            errors.push(errorValue);
          } else if (errorKey.includes("apiErrorDetails")) {
            return;
          } else if (
            typeof errorValue === "object" &&
            (errorValue as { message: string }).message
          ) {
            errors.push((errorValue as { message: string }).message);
          } else {
            errors.push(
              typeof errorValue === "string" ? errorValue : String(errorValue)
            );
          }
        }
      );
    }

    this.cellErrorsCache.set(cacheKey, errors);
    return errors;
  }

  getCachedRowHasErrors(rowData: TableDataSource): boolean {
    const cacheKey = rowData.rowIndex;
    if (this.rowHasErrorsCache.has(cacheKey)) {
      return this.rowHasErrorsCache.get(cacheKey)!;
    }

    const hasErrors =
      this.getCachedRowErrorCount(rowData) > 0 ||
      this.isRecordError(rowData.rowIndex);
    this.rowHasErrorsCache.set(cacheKey, hasErrors);
    return hasErrors;
  }

  getCachedColumnFilterState(columnKey: string): boolean {
    if (this.columnFilterCache.has(columnKey)) {
      return this.columnFilterCache.get(columnKey)!;
    }

    const filter = this.filters.columnFilters.get(columnKey);
    const isFiltered = filter
      ? filter.selectedValues.size < filter.allValues.length
      : false;
    this.columnFilterCache.set(columnKey, isFiltered);
    return isFiltered;
  }

  getCachedCellTooltip(rowData: TableDataSource, columnKey: string): string {
    const cacheKey = `${rowData.rowIndex}-${columnKey}-tooltip`;
    if (this.cellTooltipCache.has(cacheKey)) {
      return this.cellTooltipCache.get(cacheKey)!;
    }

    const errors = this.getCachedCellErrors(rowData, columnKey);
    const tooltip = errors.join("; ");
    this.cellTooltipCache.set(cacheKey, tooltip);
    return tooltip;
  }

  private calculateActiveFiltersCount(): number {
    let count = 0;

    if (this.filters.globalSearch.trim()) count++;
    if (
      this.filters.showErrorsOnly ||
      this.filters.showValidOnly ||
      this.filters.showProcessedOnly ||
      this.filters.showPendingOnly
    )
      count++;

    this.filters.columnFilters.forEach((filter) => {
      if (filter.selectedValues.size < filter.allValues.length) count++;
    });

    return count;
  }

  getCellValue(rowData: TableDataSource, columnKey: string) {
    return rowData.formGroup.get(columnKey)?.value || "";
  }

  getCellErrors(rowData: TableDataSource, columnKey: string): string[] {
    const control = rowData.formGroup.get(columnKey);
    if (!control || !control.errors) return [];

    const errors: string[] = [];

    Object.entries(control.errors).forEach(
      ([errorKey, errorValue]: [string, unknown]) => {
        if (errorKey.includes("apiError") && typeof errorValue === "string") {
          // Handle API errors specifically
          errors.push(errorValue);
        } else if (errorKey.includes("apiErrorDetails")) {
          // Skip this as it's handled by apiError
          return;
        } else if (
          typeof errorValue === "object" &&
          (errorValue as { message: string }).message
        ) {
          // If the error has a structured format with message, use it
          errors.push((errorValue as { message: string }).message);
        } else {
          // Otherwise, return the error as string
          errors.push(
            typeof errorValue === "string" ? errorValue : String(errorValue)
          );
        }
      }
    );

    return errors;
  }

  hasCellError(rowData: TableDataSource, columnKey: string): boolean {
    return this.getCellErrors(rowData, columnKey).length > 0;
  }

  getRowErrorCount(rowData: TableDataSource): number {
    let errorCount = 0;

    this.config.columns.forEach((column) => {
      const cellErrors = this.getCellErrors(rowData, column.key);
      errorCount += cellErrors.length;
    });

    return errorCount;
  }

  getTotalErrorCount(): number {
    return this.errorCollection.getTotalErrors();
  }

  /**
   * Get count of pre-validated rows
   */
  get preValidatedCount(): number {
    if (!this.dataSource || !this.config.preValidation?.enabled) {
      return 0;
    }

    return this.dataSource.filter((row) => {
      return row.formGroup.get('_isPreValidated')?.value === true;
    }).length;
  }

  /**
   * Get count of rows with pre-validation errors
   */
  get preValidationErrorCount(): number {
    if (!this.dataSource || !this.config.preValidation?.enabled) {
      return 0;
    }

    return this.dataSource.filter((row) => {
      return row.formGroup.get('_preValidationResult')?.value === 'error';
    }).length;
  }

  /**
   * Check if a column is a related column (auto-populated by pre-validation)
   * Used to highlight related columns with blue background in the UI
   */
  isRelatedColumn(column: ImportColumnConfig): boolean {
    return this.preValidationService.isRelatedColumn(column, this.config);
  }

  getInvalidRecords() {
    if (!this.dataSource) return [];

    return this.dataSource.filter(
      (rowData) =>
        this.getRowErrorCount(rowData) > 0 ||
        this.isRecordError(rowData.rowIndex)
    );
  }

  getValidRecords() {
    if (!this.dataSource) return [];

    return this.dataSource.filter(
      (rowData) => this.getRowErrorCount(rowData) === 0
    );
  }

  hasInvalidRecords(): boolean {
    return this.getInvalidRecords().length > 0;
  }

  initializeFilters(): void {
    this.filters.columnFilters.clear();
    this.clearColumnValuesCache();

    this.config.columns.forEach((column) => {
      const allValues = this.getUniqueColumnValuesOptimized(column.key);

      this.filters.columnFilters.set(column.key, {
        columnKey: column.key,
        selectedValues: new Set(allValues),
        allValues: allValues,
        filteredValues: allValues,
        searchTerm: "",
      });

      // Setup debounced search for this column
      this.setupColumnFilterSearch(column.key);
    });

    this.applyFilters();
  }

  // Optimized version for bulk operations that preserves existing filter state
  private initializeFiltersOptimized(): void {
    const previousFilters = new Map(this.filters.columnFilters);
    this.clearColumnValuesCache();

    this.config.columns.forEach((column) => {
      const allValues = this.getUniqueColumnValuesOptimized(column.key);
      const previousFilter = previousFilters.get(column.key);

      // Preserve previous selection if it exists and values still exist
      let selectedValues = new Set(allValues);
      if (
        previousFilter &&
        previousFilter.selectedValues.size < previousFilter.allValues.length
      ) {
        const validPreviousSelections = Array.from(
          previousFilter.selectedValues
        ).filter((value) => allValues.includes(value));

        if (validPreviousSelections.length > 0) {
          selectedValues = new Set(validPreviousSelections);
        }
      }

      this.filters.columnFilters.set(column.key, {
        columnKey: column.key,
        selectedValues: selectedValues,
        allValues: allValues,
        filteredValues: previousFilter?.searchTerm
          ? this.filterColumnValues(allValues, previousFilter.searchTerm)
          : allValues,
        searchTerm: previousFilter?.searchTerm || "",
      });

      // Setup debounced search for this column if not already setup
      this.setupColumnFilterSearch(column.key);
    });

    this.applyFilters();
  }

  private getUniqueColumnValues(columnKey: string): string[] {
    // Check cache first
    if (this.columnValuesCache.has(columnKey)) {
      return this.columnValuesCache.get(columnKey)!;
    }

    // Use Set for O(1) lookups and automatic deduplication
    const uniqueValues = new Set<string>();

    for (const row of this.dataSource) {
      const cellValue = this.getCellValue(row, columnKey);
      if (cellValue !== null && cellValue !== undefined && cellValue !== "") {
        uniqueValues.add(cellValue.toString());
      }
    }

    // Convert to sorted array
    const sortedValues = Array.from(uniqueValues).sort((a, b) => {
      // Try numeric sorting first
      const numA = parseFloat(a);
      const numB = parseFloat(b);

      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }

      // Fallback to string sorting
      return a.localeCompare(b, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });

    // Cache the result
    this.columnValuesCache.set(columnKey, sortedValues);

    return sortedValues;
  }

  private setupColumnFilterSearch(columnKey: string): void {
    if (this.filterSearchDebounceMap.has(columnKey)) {
      return; // Already setup
    }

    const searchSubject = new Subject<string>();
    this.filterSearchDebounceMap.set(columnKey, searchSubject);

    searchSubject
      .pipe(
        debounceTime(200), // Reduced debounce for better responsiveness
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((searchTerm) => {
        this.updateColumnFilterSearch(columnKey, searchTerm);
      });
  }

  private updateColumnFilterSearch(
    columnKey: string,
    searchTerm: string
  ): void {
    const filter = this.filters.columnFilters.get(columnKey);
    if (filter) {
      filter.searchTerm = searchTerm;
      filter.filteredValues = this.filterColumnValues(
        filter.allValues,
        searchTerm
      );
      this.cdr.markForCheck();
    }
  }

  applyFilters(): void {
    // Use optimized filtering for large datasets
    let filtered =
      this.isLargeDataset && this.dataSource.length > 10000
        ? this.applyFiltersOptimized()
        : this.applyFiltersStandard();

    // Apply sorting after filtering
    filtered = this.applySorting(filtered);

    this.filteredDataSource = filtered;
    this.virtualTableDataSource.data = filtered;
    this.filteredRecordsCount = filtered.length;
    this.activeFiltersCount = this.calculateActiveFiltersCount();
    this.cdr.markForCheck();
  }

  private applyFiltersStandard(): TableDataSource[] {
    let filtered = [...this.dataSource];

    // Apply record state filters using cached values
    if (this.filters.showErrorsOnly) {
      filtered = filtered.filter((row) => this.getCachedRowHasErrors(row));
    } else if (this.filters.showValidOnly) {
      filtered = filtered.filter(
        (row) =>
          !this.getCachedRowHasErrors(row) &&
          !this.isRecordProcessed(row.rowIndex) &&
          !this.isRecordError(row.rowIndex)
      );
    } else if (this.filters.showProcessedOnly) {
      filtered = filtered.filter((row) => this.isRecordProcessed(row.rowIndex));
    } else if (this.filters.showPendingOnly) {
      filtered = filtered.filter(
        (row) =>
          !this.isRecordProcessed(row.rowIndex) &&
          !this.isRecordError(row.rowIndex) &&
          !this.getCachedRowHasErrors(row)
      );
    }

    // Apply global search (optimized)
    if (this.filters.globalSearch.trim()) {
      const searchTerm = this.filters.globalSearch.toLowerCase().trim();
      filtered = filtered.filter((row) => {
        return this.config.columns.some((column) => {
          const cellValue = this.getCellValue(row, column.key) || "";
          return cellValue.toString().toLowerCase().includes(searchTerm);
        });
      });
    }

    // Apply column filters (optimized)
    this.filters.columnFilters.forEach((filter, columnKey) => {
      if (filter.selectedValues.size < filter.allValues.length) {
        filtered = filtered.filter((row) => {
          const cellValue = this.getCellValue(row, columnKey) || "";
          return filter.selectedValues.has(cellValue.toString());
        });
      }
    });

    return filtered;
  }

  private applyFiltersOptimized(): TableDataSource[] {
    const filtered: TableDataSource[] = [];
    const hasGlobalSearch = !!this.filters.globalSearch.trim();
    const searchTerm = hasGlobalSearch
      ? this.filters.globalSearch.toLowerCase().trim()
      : "";

    // Pre-compile column filters for faster lookup
    const activeColumnFilters = new Map<string, Set<string>>();
    this.filters.columnFilters.forEach((filter, columnKey) => {
      if (filter.selectedValues.size < filter.allValues.length) {
        activeColumnFilters.set(columnKey, filter.selectedValues);
      }
    });

    // Single pass through the data with optimized checks
    for (let i = 0; i < this.dataSource.length; i++) {
      const row = this.dataSource[i];
      const rowIndex = row.rowIndex;

      // Fast state filter checks using sets
      if (this.filters.showErrorsOnly && !this.errorRowsSet.has(rowIndex))
        continue;
      if (
        this.filters.showProcessedOnly &&
        !this.processedRecordIndices.has(rowIndex)
      )
        continue;
      if (
        this.filters.showValidOnly &&
        (this.errorRowsSet.has(rowIndex) ||
          this.processedRecordIndices.has(rowIndex) ||
          this.isRecordError(rowIndex))
      )
        continue;
      if (
        this.filters.showPendingOnly &&
        (this.errorRowsSet.has(rowIndex) ||
          this.processedRecordIndices.has(rowIndex) ||
          this.isRecordError(rowIndex))
      )
        continue;

      // Fast global search
      if (hasGlobalSearch) {
        let matchesSearch = false;
        for (const column of this.config.columns) {
          const cellValue = this.getCellValue(row, column.key) || "";
          if (cellValue.toString().toLowerCase().includes(searchTerm)) {
            matchesSearch = true;
            break;
          }
        }
        if (!matchesSearch) continue;
      }

      // Fast column filters
      let passesColumnFilters = true;
      for (const [columnKey, selectedValues] of activeColumnFilters) {
        const cellValue = this.getCellValue(row, columnKey) || "";
        if (!selectedValues.has(cellValue.toString())) {
          passesColumnFilters = false;
          break;
        }
      }
      if (!passesColumnFilters) continue;

      filtered.push(row);
    }

    return filtered;
  }

  onGlobalSearchChange(searchTerm: string): void {
    this.searchSubject.next(searchTerm);
  }

  onErrorFilterChange(
    filterType: "errors" | "valid" | "processed" | "pending" | "all"
  ): void {
    this.filters.showErrorsOnly = filterType === "errors";
    this.filters.showValidOnly = filterType === "valid";
    this.filters.showProcessedOnly = filterType === "processed";
    this.filters.showPendingOnly = filterType === "pending";
    this.applyFilters();
  }

  getCurrentFilterValue(): string {
    if (this.filters.showErrorsOnly) return "errors";
    if (this.filters.showValidOnly) return "valid";
    if (this.filters.showProcessedOnly) return "processed";
    if (this.filters.showPendingOnly) return "pending";
    return "all";
  }

  onColumnFilterChange(columnKey: string, selectedValues: string[]): void {
    const filter = this.filters.columnFilters.get(columnKey);
    if (filter) {
      filter.selectedValues = new Set(selectedValues);
      this.columnFilterCache.delete(columnKey);
      this.applyFilters();
    }
  }

  toggleColumnFilterValue(
    columnKey: string,
    value: string,
    checked: boolean
  ): void {
    const filter = this.filters.columnFilters.get(columnKey);
    if (filter) {
      if (checked) {
        filter.selectedValues.add(value);
      } else {
        filter.selectedValues.delete(value);
      }
      this.columnFilterCache.delete(columnKey);
      this.applyFilters();
    }
  }

  resetAllFilters(): void {
    this.filters.globalSearch = "";
    this.filters.showErrorsOnly = false;
    this.filters.showValidOnly = false;
    this.filters.showProcessedOnly = false;
    this.filters.showPendingOnly = false;

    this.filters.columnFilters.forEach((filter) => {
      filter.selectedValues = new Set(filter.allValues);
      filter.searchTerm = "";
      filter.filteredValues = filter.allValues;
    });

    this.clearAllCaches();
    this.applyFilters();
  }

  getColumnFilterValues(columnKey: string): string[] {
    const filter = this.filters.columnFilters.get(columnKey);
    return filter ? filter.allValues : [];
  }

  getSelectedColumnFilterValues(columnKey: string): string[] {
    const filter = this.filters.columnFilters.get(columnKey);
    return filter ? Array.from(filter.selectedValues) : [];
  }

  isColumnFiltered(columnKey: string): boolean {
    const filter = this.filters.columnFilters.get(columnKey);
    return filter
      ? filter.selectedValues.size < filter.allValues.length
      : false;
  }

  getActiveFiltersCount(): number {
    let count = 0;

    if (this.filters.globalSearch.trim()) count++;
    if (
      this.filters.showErrorsOnly ||
      this.filters.showValidOnly ||
      this.filters.showProcessedOnly ||
      this.filters.showPendingOnly
    )
      count++;

    this.filters.columnFilters.forEach((filter) => {
      if (filter.selectedValues.size < filter.allValues.length) count++;
    });

    return count;
  }

  getColumnDisplayName(columnKey: string): string {
    const column = this.config.columns.find((col) => col.key === columnKey);
    return column ? column.displayName : columnKey;
  }

  // Column filter search methods
  getColumnFilterSearch(columnKey: string): string {
    const filter = this.filters.columnFilters.get(columnKey);
    return filter ? filter.searchTerm : "";
  }

  onColumnFilterSearchChange(columnKey: string, searchTerm: string): void {
    const searchSubject = this.filterSearchDebounceMap.get(columnKey);
    if (searchSubject) {
      searchSubject.next(searchTerm);
    }
  }

  private filterColumnValues(values: string[], searchTerm: string): string[] {
    if (!searchTerm.trim()) {
      return values;
    }

    const term = searchTerm.toLowerCase().trim();

    // Optimized filtering with early exit for exact matches
    const filtered: string[] = [];
    for (const value of values) {
      const lowerValue = value.toLowerCase();
      if (lowerValue.includes(term)) {
        filtered.push(value);
        // Prioritize exact matches by putting them first
        if (lowerValue === term) {
          filtered.unshift(filtered.pop()!);
        }
      }
    }

    return filtered;
  }

  // Template helper methods optimized for large datasets
  trackByValue = (index: number, value: string): string => {
    return value;
  };

  trackByRowIndex = (index: number, row: TableDataSource): number => {
    return row.rowIndex;
  };

  // Memoized methods to reduce change detection cycles
  private highlightCache = new Map<string, boolean>();

  isValueHighlighted = (columnKey: string, value: string): boolean => {
    const cacheKey = `${columnKey}-${value}`;
    if (this.highlightCache.has(cacheKey)) {
      return this.highlightCache.get(cacheKey)!;
    }

    const searchTerm = this.getColumnFilterSearch(columnKey);
    const result =
      searchTerm.length > 0 &&
      value.toLowerCase().includes(searchTerm.toLowerCase());
    this.highlightCache.set(cacheKey, result);
    return result;
  };

  private highlightTextCache = new Map<string, string>();

  highlightSearchTerm = (value: string, searchTerm: string): string => {
    if (!searchTerm.trim() || !value) {
      return value || "(vacío)";
    }

    const cacheKey = `${value}-${searchTerm}`;
    if (this.highlightTextCache.has(cacheKey)) {
      return this.highlightTextCache.get(cacheKey)!;
    }

    const displayValue = value || "(vacío)";
    const regex = new RegExp(`(${this.escapeRegExp(searchTerm)})`, "gi");
    const result = displayValue.replace(
      regex,
      '<mark class="bg-yellow-200">$1</mark>'
    );
    this.highlightTextCache.set(cacheKey, result);
    return result;
  };

  private escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  // High-performance methods for large datasets (28k+ records)
  private buildColumnIndexMap(): void {
    this.config.columns.forEach((column, index) => {
      this.columnIndexMap.set(column.key, index);
    });
  }

  private async validateCurrentSheetBatched(): Promise<void> {
    if (this.isValidationInProgress) return;

    this.isValidationInProgress = true;
    this.validationQueue = [];

    // Create validation batches
    const totalRows = this.dataSource.length;
    this.totalValidationBatches = Math.ceil(
      totalRows / ImportDialogComponent.VALIDATION_BATCH_SIZE
    );

    for (
      let i = 0;
      i < totalRows;
      i += ImportDialogComponent.VALIDATION_BATCH_SIZE
    ) {
      const endIndex = Math.min(
        i + ImportDialogComponent.VALIDATION_BATCH_SIZE,
        totalRows
      );
      this.validationQueue.push({ startIndex: i, endIndex });
    }

    // Process batches with requestAnimationFrame to avoid blocking UI
    await this.processBatchedValidation();

    this.isValidationInProgress = false;
    this.processedValidationBatches = 0;

    // Update UI after all validation is complete
    this.updateAllCachedValues();
    this.cdr.markForCheck();
  }

  private async processBatchedValidation(): Promise<void> {
    return new Promise((resolve) => {
      const processBatch = () => {
        if (this.validationQueue.length === 0) {
          resolve();
          return;
        }

        const batch = this.validationQueue.shift()!;
        this.validateBatch(batch.startIndex, batch.endIndex);

        this.processedValidationBatches++;

        // Update progress if needed
        if (this.progress) {
          this.progress.percentage = Math.round(
            (this.processedValidationBatches / this.totalValidationBatches) *
              100
          );
          this.progress.message = `Validando... ${this.processedValidationBatches}/${this.totalValidationBatches} lotes`;
        }

        // Use requestAnimationFrame to avoid blocking UI
        requestAnimationFrame(processBatch);
      };

      processBatch();
    });
  }

  private validateBatch(startIndex: number, endIndex: number): void {
    for (let i = startIndex; i < endIndex; i++) {
      const rowData = this.dataSource[i];
      if (!rowData) continue;

      const formGroup = rowData.formGroup;
      let hasErrors = false;

      this.config.columns.forEach((column) => {
        const control = formGroup.get(column.key);
        if (control) {
          // Clear previous errors first
          control.setErrors(null);

          // Apply validators
          const errors = this.validationService.validateField(
            control.value,
            column,
            rowData.data
          );

          if (errors && Object.keys(errors).length > 0) {
            control.setErrors(errors);
            hasErrors = true;
          }
        }
      });

      // Update error tracking
      if (hasErrors) {
        this.errorRowsSet.add(i);
      } else {
        this.errorRowsSet.delete(i);
      }
    }
  }

  // Optimized method to get unique column values for large datasets
  private getUniqueColumnValuesOptimized(columnKey: string): string[] {
    // Check cache first
    if (this.columnValuesCache.has(columnKey)) {
      return this.columnValuesCache.get(columnKey)!;
    }

    // For large datasets, use sampling to avoid memory issues
    if (this.isLargeDataset && this.dataSource.length > 10000) {
      return this.getUniqueColumnValuesSampled(columnKey);
    }

    return this.getUniqueColumnValues(columnKey);
  }

  private getUniqueColumnValuesSampled(
    columnKey: string,
    sampleSize = 2000
  ): string[] {
    const uniqueValues = new Set<string>();
    const step = Math.max(1, Math.floor(this.dataSource.length / sampleSize));

    // Sample every nth row to get representative unique values
    for (let i = 0; i < this.dataSource.length; i += step) {
      const cellValue = this.getCellValue(this.dataSource[i], columnKey);
      if (cellValue !== null && cellValue !== undefined && cellValue !== "") {
        uniqueValues.add(cellValue.toString());
      }

      // If we already have enough unique values, stop sampling
      if (uniqueValues.size >= sampleSize / 2) {
        break;
      }
    }

    const sortedValues = Array.from(uniqueValues).sort((a, b) => {
      const numA = parseFloat(a);
      const numB = parseFloat(b);

      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }

      return a.localeCompare(b, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });

    // Cache the result
    this.columnValuesCache.set(columnKey, sortedValues);

    return sortedValues;
  }


  getFilteredColumnValues(columnKey: string): string[] {
    const filter = this.filters.columnFilters.get(columnKey);
    return filter ? filter.filteredValues : [];
  }

  showRowErrors(rowData: TableDataSource): void {
    const formGroup = rowData.formGroup as FormGroup;
    const errors: { [columnKey: string]: unknown } = {};

    // Collect all errors for this row
    this.config.columns.forEach((column) => {
      const control = formGroup.get(column.key);
      if (control && control.errors) {
        errors[column.key] = control.errors;
      }
    });

    // Open the row errors dialog
    this.dialog.open(RowErrorsDialogComponent, {
      data: {
        rowIndex: rowData.rowIndex,
        rowData: rowData.data,
        columns: this.config.columns,
        errors: errors,
      },
      width: "600px",
      maxHeight: "80vh",
    });
  }

  isColumnRequired(columnKey: string): boolean {
    const column = this.config.columns.find((col) => col.key === columnKey);
    return column ? column.required : false;
  }

  onCellValueChange(
    rowData: TableDataSource,
    columnKey: string,
    value: unknown
  ): void {
    const control = rowData.formGroup.get(columnKey);
    if (control) {
      control.setValue(value);
      control.markAsTouched();

      // Re-validate the form after changes
      setTimeout(() => this.validateCurrentSheet(), 100);
    }
  }

  private resetModalState(): void {
    // Reset all state completely
    this.progress = null;
    this.processedFile = null;
    this.currentSheet = null;
    this.formArray = null;
    this.isFileSelected = false;
    this.selectedFile = null;
    this.dataSource = [];
    this.filteredDataSource = [];
    this.virtualTableDataSource.data = [];
    this.errorCollection.clear();

    // Reset record state tracking
    this.recordStates.clear();
    this.processedRecordIndices.clear();

    // Reset cached values
    this.totalRecords = 0;
    this.filteredRecordsCount = 0;
    this.invalidRecordsCount = 0;
    this.validRecordsCount = 0;
    this.processedRecordsCount = 0;
    this.unprocessedValidRecordsCount = 0;
    this.activeFiltersCount = 0;
    this.totalErrorCount = 0;

    // Clear all caches
    this.clearAllCaches();

    // Reset filters
    this.filters = {
      globalSearch: "",
      showErrorsOnly: false,
      showValidOnly: false,
      showProcessedOnly: false,
      showPendingOnly: false,
      columnFilters: new Map(),
    };

    // Reset UI state
    this.isProcessing = false;
    this.isValidating = false;
    this.isImporting = false;
    this.progress = null;

    // Reset pre-validation state
    this.isPreValidating = false;
    this.preValidationCompleted = false;
    this.preValidationFailed = false;
    this.preValidationErrorMessage = '';
    this.preValidatedRowCount = 0;
    this.skipPreValidation = false;
    if (this.currentPreValidationController) {
      this.currentPreValidationController.abort();
      this.currentPreValidationController = null;
    }

    // Reset column visibility state
    this.emptyOptionalColumns.clear();
    this.columnVisibilityMap.clear();
    this.showColumnPanel = false;
    this.hiddenColumnsCount = 0;

    this.cdr.markForCheck();
  }

  async reUploadFile(): Promise<void> {
    // Reset state first
    this.resetModalState();

    // Wait for DOM to update
    setTimeout(() => {
      // Trigger file selection with better browser compatibility
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".xlsx,.xls";
      input.multiple = false;

      // Add to DOM temporarily for better browser support
      input.style.display = "none";
      document.body.appendChild(input);

      input.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          this.handleFile(file);
        }
        // Clean up
        document.body.removeChild(input);
      };

      // Handle cancel case
      input.oncancel = () => {
        document.body.removeChild(input);
      };

      // Trigger click
      input.click();
    }, 100);
  }

  async executeImport(): Promise<void> {
    if (!this.canImport()) {
      return;
    }

    try {
      this.isImporting = true;

      // Only get unprocessed valid records for import
      const unprocessedValidRecords = this.getUnprocessedValidRecords();
      let data = unprocessedValidRecords.map((record) => {
        const cleanRow = { ...record.formGroup.value };
        delete cleanRow._rowIndex;
        delete cleanRow._isValid;
        delete cleanRow._errors;
        delete cleanRow._isPreValidated;
        delete cleanRow._preValidationResult;
        return cleanRow;
      });

      // Filter payload to exclude related columns (FR-20)
      if (this.config.preValidation?.enabled) {
        data = this.preValidationService.filterImportPayload(data, this.config);
      }

      const metadata = this.apiService.createImportMetadata(
        this.selectedFile!.name,
        this.currentSheet!.name,
        data,
        this.config.columns.map((col) => col.key)
      );

      const response = await this.apiService.executeImport(
        this.config.apiEndpoint!,
        data,
        metadata,
        this.config.customImportHandler
      );

      if (response.success) {
        this.handleApiResponse(response as ApiResponse);
        this.showSuccess("Importación completada exitosamente");
      } else {
        this.handleApiResponse(response as ApiResponse);
      }
    } catch (error) {
      this.showError(
        `Error durante la importación: ${error?.error ?? error?.message ?? "Error desconocido"}`
      );
    } finally {
      this.isImporting = false;
    }
  }

  private handleApiResponse(response: ApiResponse): void {
    const processResult = response.data.processResult;

    // Process API errors and update form controls first
    processResult.invalidRecords.forEach((invalidRecord) => {
      const frontendIndex = invalidRecord.index - 1; // Convert to 0-based index
      const formGroup = this.formArray?.at(frontendIndex) as FormGroup;

      if (formGroup) {
        invalidRecord.columns.forEach((columnError) => {
          const control = formGroup.get(columnError.key);
          if (control) {
            columnError.errors.forEach((error, index) => {
              control.setErrors({
                ...control.errors,
                [`apiError${index}`]: error.message,
              });
            });

            control.markAsTouched();
          }
        });
      }
    });

    // Now determine record states based on whether they have any errors after API processing
    this.recordStates.clear();
    this.processedRecordIndices.clear();

    for (let i = 0; i < processResult.totalRecords; i++) {
      const recordIndex = i;
      const formGroup = this.formArray?.at(recordIndex) as FormGroup;

      if (formGroup) {
        // Check if this record has any errors (frontend validation or API errors)
        let hasAnyErrors = false;

        this.config.columns.forEach((column) => {
          const control = formGroup.get(column.key);
          if (control && Object.keys(control?.errors || {}).length > 0) {
            hasAnyErrors = true;
          }
        });

        if (hasAnyErrors) {
          this.recordStates.set(recordIndex, "error");
        } else {
          this.recordStates.set(recordIndex, "processed");
          this.processedRecordIndices.add(recordIndex);
        }
      }
    }

    // Update error collection
    this.validateCurrentSheet();

    // Update cached values and apply filters
    this.updateAllCachedValues();
    this.applyFilters();

    // Show summary message
    const successCount = processResult?.validRecords || 0;
    const errorCount = processResult?.errorCount || 0;

    if (errorCount > 0) {
      this.showWarning(
        `Importación parcial: ${successCount} registros procesados, ${errorCount} con errores`
      );
    } else {
      this.showSuccess(
        `Importación exitosa: ${successCount} registros procesados`
      );
    }
  }

  canImport(): boolean {
    const hasUnprocessedValidRecords =
      this.getUnprocessedValidRecords().length > 0;
    const hasFrontendErrors = this.getInvalidRecords().length > 0;
    const hasProcessedRecords = this.hasProcessedRecords();

    // Basic import checks
    const basicChecks =
      this.formArray &&
      !this.isImporting &&
      !this.isProcessing &&
      hasUnprocessedValidRecords &&
      !hasFrontendErrors &&
      !hasProcessedRecords &&
      this.config.apiEndpoint;

    if (!basicChecks) {
      return false;
    }

    // Pre-validation checks (FR-18)
    if (this.config.preValidation?.enabled) {
      // If skipPreValidation is checked and pre-validation is optional, allow import
      if (this.skipPreValidation && !this.config.preValidation.required) {
        return true;
      }

      // If pre-validation is required, must be completed successfully
      if (this.config.preValidation.required && !this.preValidationCompleted) {
        return false;
      }

      // If blockImportOnErrors is true, check for pre-validation errors
      if (
        this.config.preValidation.blockImportOnErrors &&
        this.preValidationCompleted
      ) {
        const hasPreValidationErrors = this.dataSource.some((row) => {
          const preValidationResult =
            row.formGroup.get('_preValidationResult')?.value;
          return preValidationResult === 'error';
        });

        if (hasPreValidationErrors) {
          return false;
        }
      }
    }

    return true;
  }

  getUnprocessedValidRecords() {
    if (!this.dataSource) return [];

    return this.dataSource.filter((rowData) => {
      const isValid = this.getRowErrorCount(rowData) === 0;
      const isNotProcessed = !this.processedRecordIndices.has(rowData.rowIndex);
      return isValid && isNotProcessed;
    });
  }

  hasProcessedRecords(): boolean {
    return this.processedRecordIndices.size > 0;
  }

  getRecordState(rowIndex: number): RecordState {
    return this.recordStates.get(rowIndex) || "pending";
  }

  isRecordProcessed(rowIndex: number): boolean {
    return this.processedRecordIndices.has(rowIndex);
  }

  isRecordError(rowIndex: number): boolean {
    return this.recordStates.get(rowIndex) === "error";
  }

  async checkForProcessedRecordsAndImport(): Promise<void> {
    if (
      this.hasProcessedRecords() &&
      this.getUnprocessedValidRecords().length === 0
    ) {
      this.showError(
        "No hay registros pendientes para importar. Todos los registros válidos ya han sido procesados."
      );
      return;
    }

    if (this.hasProcessedRecords()) {
      this.showError(
        "No se puede importar mientras existan registros procesados. Por favor, elimine los registros procesados antes de continuar."
      );
      return;
    }

    const invalidRecords = this.getInvalidRecords();
    if (invalidRecords.length > 0) {
      const apiErrors = this.getApiErrorRecords().length;
      const frontendErrors = this.getFrontendErrorRecords().length;

      let errorMessage = "No se puede importar mientras existan errores. ";
      if (apiErrors > 0 && frontendErrors > 0) {
        errorMessage += `Hay ${apiErrors} registros con errores de API y ${frontendErrors} registros con errores de validación.`;
      } else if (apiErrors > 0) {
        errorMessage += `Hay ${apiErrors} registros con errores de API.`;
      } else {
        errorMessage += `Hay ${frontendErrors} registros con errores de validación.`;
      }
      errorMessage +=
        " Por favor, corrija los errores o elimine los registros inválidos.";

      this.showError(errorMessage);
      return;
    }

    await this.executeImport();
  }

  cancel(): void {
    this.dialogRef.close({ cancelled: true });
  }

  // Utility methods for user feedback
  private showSuccess(message: string): void {
    this.snackBar.open(message, "Cerrar", {
      duration: 3000,
      panelClass: ["success-snackbar"],
    });
  }

  private showError(message: string): void {
    this.snackBar.open(message, "Cerrar", {
      duration: 5000,
      panelClass: ["error-snackbar"],
    });
  }

  private showWarning(message: string): void {
    this.snackBar.open(message, "Cerrar", {
      duration: 4000,
      panelClass: ["warning-snackbar"],
    });
  }

  private showErrors(errors: string[]): void {
    errors.forEach((error) => this.showError(error));
  }

  openInvalidRecordsMenu(): void {
    // Will be implemented with menu trigger
  }

  removeInvalidRecords(): void {
    if (!this.formArray || !this.currentSheet) return;

    const invalidRecords = this.getInvalidRecords();
    if (invalidRecords.length === 0) {
      this.showWarning("No hay registros inválidos para eliminar");
      return;
    }

    // Create new data source with only valid records
    const validRecords = this.getValidRecords();

    // Update form array with valid records only
    const validFormGroups = validRecords.map((record) => record.formGroup);

    // Clear and rebuild form array
    this.formArray.clear();
    validFormGroups.forEach((formGroup) => {
      this.formArray!.push(formGroup);
    });

    const newProcessedRecordIndices = new Set<number>();

    // Update data source
    this.dataSource = validRecords.map((record, index) => {
      if (this.isRecordProcessed(record.rowIndex)) {
        newProcessedRecordIndices.add(index);
      }

      return {
        ...record,
        rowIndex: index,
      };
    });

    // Update current sheet data
    this.currentSheet.processedData = validRecords.map((record) => record.data);

    this.processedRecordIndices = newProcessedRecordIndices;

    // Clear error collection for removed records
    this.errorCollection.clear();
    this.recordStates.clear();

    // Reinitialize filters with new data - optimized for bulk operations
    this.initializeFiltersOptimized();

    // Re-validate to ensure error collection is updated
    this.validateCurrentSheet();

    this.showSuccess(
      `Se eliminaron ${invalidRecords.length} registros inválidos`
    );
  }

  exportInvalidRecordsToExcel(): void {
    const invalidRecords = this.getInvalidRecords();
    if (invalidRecords.length === 0) {
      this.showWarning("No hay registros inválidos para exportar");
      return;
    }

    // Create Excel data with error information
    const excelData = invalidRecords.map((record, index) => {
      const rowData = {
        "Fila Original": record.rowIndex + 1,
        Errores: this.getRowErrorCount(record),
      };

      // Add column data
      this.config.columns.forEach((column) => {
        rowData[column.displayName] =
          this.getCellValue(record, column.key) || "";

        // Add error details for this column
        const cellErrors = this.getCellErrors(record, column.key);
        if (cellErrors.length > 0) {
          rowData[`Errores_${column.displayName}`] = cellErrors.join("; ");
        }
      });

      return rowData;
    });

    // Create and download Excel file
    this.excelProcessor.exportToExcel(
      excelData,
      `registros_invalidos_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
    this.showSuccess("Registros inválidos exportados a Excel");
  }

  copyInvalidRecordsToClipboard(): void {
    const invalidRecords = this.getInvalidRecords();
    if (invalidRecords.length === 0) {
      this.showWarning("No hay registros inválidos para copiar");
      return;
    }

    // Create text format for clipboard
    let clipboardText = "REGISTROS INVÁLIDOS:\n\n";

    invalidRecords.forEach((record, index) => {
      clipboardText += `Fila ${record.rowIndex + 1} (${this.getRowErrorCount(record)} errores):\n`;

      this.config.columns.forEach((column) => {
        const value = this.getCellValue(record, column.key) || "-";
        const errors = this.getCellErrors(record, column.key);

        clipboardText += `  ${column.displayName}: ${value}`;
        if (errors.length > 0) {
          clipboardText += ` [ERRORES: ${errors.join(", ")}]`;
        }
        clipboardText += "\n";
      });

      clipboardText += "\n";
    });

    // Copy to clipboard
    navigator.clipboard
      .writeText(clipboardText)
      .then(() => {
        this.showSuccess(
          "Lista de registros inválidos copiada al portapapeles"
        );
      })
      .catch(() => {
        this.showError("Error al copiar al portapapeles");
      });
  }

  getProcessedRecords() {
    if (!this.dataSource) return [];

    return this.dataSource.filter((rowData) =>
      this.isRecordProcessed(rowData.rowIndex)
    );
  }

  getApiErrorRecords() {
    if (!this.dataSource) return [];

    return this.dataSource.filter((rowData) =>
      this.isRecordError(rowData.rowIndex)
    );
  }

  getFrontendErrorRecords() {
    if (!this.dataSource) return [];

    return this.dataSource.filter(
      (rowData) =>
        this.getRowErrorCount(rowData) > 0 &&
        !this.isRecordError(rowData.rowIndex)
    );
  }

  removeProcessedRecords(): void {
    if (!this.formArray || !this.currentSheet) return;

    const processedRecords = this.getProcessedRecords();
    if (processedRecords.length === 0) {
      this.showWarning("No hay registros procesados para eliminar");
      return;
    }

    // Create new data source without processed records
    const unprocessedRecords = this.dataSource.filter(
      (rowData) => !this.isRecordProcessed(rowData.rowIndex)
    );

    // Update form array with unprocessed records only
    const unprocessedFormGroups = unprocessedRecords.map(
      (record) => record.formGroup
    );

    // Clear and rebuild form array
    this.formArray.clear();
    unprocessedFormGroups.forEach((formGroup) => {
      this.formArray!.push(formGroup);
    });

    // Update data source
    this.dataSource = unprocessedRecords.map((record, index) => ({
      ...record,
      rowIndex: index,
    }));

    // Update current sheet data
    this.currentSheet.processedData = unprocessedRecords.map(
      (record) => record.data
    );

    // Update record state tracking - remove processed records but keep error states
    const newRecordStates = new Map<number, RecordState>();
    unprocessedRecords.forEach((record, newIndex) => {
      const oldState = this.recordStates.get(record.rowIndex);
      if (oldState === "error") {
        newRecordStates.set(newIndex, "error");
      }
    });

    this.recordStates = newRecordStates;
    this.processedRecordIndices.clear();

    // Reinitialize filters with new data - optimized for bulk operations
    this.initializeFiltersOptimized();

    // Re-validate to ensure error collection is updated
    this.validateCurrentSheet();

    this.showSuccess(
      `Se eliminaron ${processedRecords.length} registros procesados`
    );
  }

  exportProcessedRecordsToExcel(): void {
    const processedRecords = this.getProcessedRecords();
    if (processedRecords.length === 0) {
      this.showWarning("No hay registros procesados para exportar");
      return;
    }

    // Create Excel data
    const excelData = processedRecords.map((record, index) => {
      const rowData = {
        "Fila Original": record.rowIndex + 1,
        Estado: "Procesado",
      };

      // Add column data
      this.config.columns.forEach((column) => {
        rowData[column.displayName] =
          this.getCellValue(record, column.key) || "";
      });

      return rowData;
    });

    // Create and download Excel file
    this.excelProcessor.exportToExcel(
      excelData,
      `registros_procesados_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
    this.showSuccess("Registros procesados exportados a Excel");
  }

  // Template optimization: Methods to replace function calls in HTML
  // These return cached values or use cached computations

  // Replaces getRowErrorCount(rowData) calls
  getRowErrorCountOptimized(rowData: TableDataSource): number {
    return this.getCachedRowErrorCount(rowData);
  }

  // Replaces hasCellError(rowData, columnKey) calls
  hasCellErrorOptimized(rowData: TableDataSource, columnKey: string): boolean {
    return this.getCachedCellErrors(rowData, columnKey).length > 0;
  }

  // Replaces getCellErrors(rowData, columnKey).join('; ') calls
  getCellErrorsTooltipOptimized(
    rowData: TableDataSource,
    columnKey: string
  ): string {
    return this.getCachedCellTooltip(rowData, columnKey);
  }

  // Replaces isColumnFiltered(columnKey) calls
  isColumnFilteredOptimized(columnKey: string): boolean {
    return this.getCachedColumnFilterState(columnKey);
  }

  // Replaces getActiveFiltersCount() calls
  getActiveFiltersCountOptimized(): number {
    return this.activeFiltersCount;
  }

  // Replaces hasInvalidRecords() calls
  hasInvalidRecordsOptimized(): boolean {
    return this.invalidRecordsCount > 0;
  }

  // Replaces getUnprocessedValidRecords().length calls
  getUnprocessedValidRecordsCountOptimized(): number {
    return this.unprocessedValidRecordsCount;
  }

  // Replaces getTotalErrorCount() calls
  getTotalErrorCountOptimized(): number {
    return this.totalErrorCount;
  }

  // Replaces hasProcessedRecords() calls
  hasProcessedRecordsOptimized(): boolean {
    return this.processedRecordsCount > 0;
  }

  // Method to set the CSS custom property for grid columns
  private setGridColumnsCount(): void {
    if (this.config?.columns?.length) {
      document.documentElement.style.setProperty(
        "--data-columns-count",
        this.config.columns.length.toString()
      );
    }
  }

  private initializeDefaultSorting(): void {
    if (this.config.defaultSortColumn && this.config.defaultSortDirection) {
      this.sortColumn = this.config.defaultSortColumn;
      this.sortDirection = this.config.defaultSortDirection;
    }
  }

  onColumnSort(columnKey: string): void {
    if (this.isColumnSortable(columnKey)) {
      if (this.sortColumn === columnKey) {
        if (this.sortDirection === 'asc') {
          this.sortDirection = 'desc';
        } else if (this.sortDirection === 'desc') {
          this.sortColumn = null;
          this.sortDirection = 'asc';
        }
      } else {
        this.sortColumn = columnKey;
        this.sortDirection = 'asc';
      }

      this.applyFilters();
    }
  }

  private isColumnSortable(columnKey: string): boolean {
    if (this.config.sortableColumns && this.config.sortableColumns.length > 0) {
      return this.config.sortableColumns.includes(columnKey);
    }
    return true;
  }

  private applySorting(data: TableDataSource[]): TableDataSource[] {
    if (!this.sortColumn || data.length === 0) {
      return data;
    }

    return this.isLargeDataset && data.length > 10000
      ? this.applySortingOptimized(data)
      : this.applySortingStandard(data);
  }

  private applySortingStandard(data: TableDataSource[]): TableDataSource[] {
    return [...data].sort((a, b) => {
      const valueA = this.getSortValue(a, this.sortColumn!);
      const valueB = this.getSortValue(b, this.sortColumn!);

      return this.compareValues(valueA, valueB, this.sortDirection);
    });
  }

  private applySortingOptimized(data: TableDataSource[]): TableDataSource[] {

    const indexedData = data.map((item, index) => ({
      index,
      sortValue: this.getSortValue(item, this.sortColumn!)
    }));

    indexedData.sort((a, b) => this.compareValues(a.sortValue, b.sortValue, this.sortDirection));

    return indexedData.map(item => data[item.index]);
  }

  private getSortValue(row: TableDataSource, columnKey: string) {
    const value = row.data[columnKey];

    if (value == null) return '';

    const stringValue = String(value).trim();

    const numericValue = parseFloat(stringValue);
    if (!isNaN(numericValue) && isFinite(numericValue)) {
      return numericValue;
    }

    return stringValue.toLowerCase();
  }

  private compareValues(a, b, direction: 'asc' | 'desc'): number {
    let comparison = 0;

    if (a < b) {
      comparison = -1;
    } else if (a > b) {
      comparison = 1;
    }

    return direction === 'desc' ? -comparison : comparison;
  }

  isSortedBy(columnKey: string): boolean {
    return this.sortColumn === columnKey;
  }

  getSortDirection(columnKey: string): 'asc' | 'desc' | null {
    return this.isSortedBy(columnKey) ? this.sortDirection : null;
  }

  getSortIcon(columnKey: string): string {
    if (!this.isSortedBy(columnKey)) return '';
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  // ==================================================================================
  // Column Visibility Management
  // ==================================================================================

  /**
   * Analiza qué columnas opcionales están completamente vacías
   * Las columnas requeridas NUNCA se ocultan automáticamente
   */
  private analyzeEmptyColumns(): void {
    this.emptyOptionalColumns.clear();

    // Solo analizar columnas OPCIONALES
    const optionalColumns = this.config.columns.filter(col => !col.required);

    optionalColumns.forEach(column => {
      let isEmpty = true;

      // Optimización: early exit al encontrar primer valor no vacío
      for (const row of this.dataSource) {
        const value = this.getCellValue(row, column.key);

        // Check si el valor es no vacío
        if (value !== null &&
            value !== undefined &&
            value !== '' &&
            (typeof value !== 'string' || value.trim() !== '')) {
          isEmpty = false;
          break; // ✅ No revisar más registros de esta columna
        }
      }

      if (isEmpty) {
        this.emptyOptionalColumns.add(column.key);
      }
    });

    // Inicializar visibilidad basada en el análisis
    this.initializeColumnVisibility();
  }

  /**
   * Inicializa el mapa de visibilidad según la lógica:
   * - Requeridas: siempre visibles
   * - Opcionales vacías: ocultas por defecto
   * - Opcionales con datos: visibles
   */
  private initializeColumnVisibility(): void {
    this.columnVisibilityMap.clear();

    this.config.columns.forEach(column => {
      if (column.required) {
        // ✅ Columnas requeridas SIEMPRE visibles
        this.columnVisibilityMap.set(column.key, true);
      } else if (this.emptyOptionalColumns.has(column.key)) {
        // ❌ Columnas opcionales vacías ocultas por defecto
        this.columnVisibilityMap.set(column.key, false);
      } else {
        // ✅ Columnas opcionales con datos visibles
        this.columnVisibilityMap.set(column.key, true);
      }
    });

    // ✅ Related columns (auto-populated from pre-validation) always visible by default
    this.relatedColumnsMetadata.forEach((_, key) => {
      this.columnVisibilityMap.set(key, true);
    });

    this.updateDisplayedColumns();
  }

  /**
   * Actualiza las columnas mostradas en la tabla según visibilidad
   */
  private updateDisplayedColumns(): void {
    const visibleColumns = this.config.columns
      .filter(col => this.columnVisibilityMap.get(col.key) === true)
      .map(col => col.key);

    // Include visible related columns (auto-populated from pre-validation)
    const visibleRelatedColumns = Array.from(this.relatedColumnsMetadata.keys())
      .filter(key => this.columnVisibilityMap.get(key) !== false); // Default to visible if not set

    // Reconstruir displayedColumns con rowNumber, columnas visibles, related columns y actions
    this.displayedColumns = [
      'rowNumber',
      ...visibleColumns,
      ...visibleRelatedColumns,
      'actions'
    ];

    // Actualizar contador cache (include related columns in count)
    const totalColumns = this.config.columns.length + this.relatedColumnsMetadata.size;
    this.hiddenColumnsCount = totalColumns - visibleColumns.length - visibleRelatedColumns.length;

    this.cdr.markForCheck();
    this.autoResizeTableColumns();
  }

  /**
   * Toggle visibilidad de una columna específica
   */
  toggleColumnVisibility(columnKey: string): void {
    const currentState = this.columnVisibilityMap.get(columnKey);
    this.columnVisibilityMap.set(columnKey, !currentState);
    this.updateDisplayedColumns();
  }

  /**
   * Muestra TODAS las columnas (requeridas y opcionales)
   */
  showAllColumns(): void {
    // Show all config columns
    this.config.columns.forEach(column => {
      this.columnVisibilityMap.set(column.key, true);
    });

    // Show all related columns (auto-populated from pre-validation)
    this.relatedColumnsMetadata.forEach((_, key) => {
      this.columnVisibilityMap.set(key, true);
    });

    this.updateDisplayedColumns();
  }

  /**
   * Restablece al comportamiento por defecto:
   * - Requeridas: visibles
   * - Opcionales vacías: ocultas
   * - Opcionales con datos: visibles
   */
  resetColumnVisibility(): void {
    this.initializeColumnVisibility();
  }

  /**
   * Obtiene el número de columnas ocultas (para badge)
   */
  getHiddenColumnsCount(): number {
    return this.hiddenColumnsCount;
  }

  /**
   * Obtiene el número de columnas visibles
   */
  getVisibleColumnsCount(): number {
    return this.config.columns.length - this.hiddenColumnsCount;
  }

  /**
   * Verifica si una columna está vacía (opcional y sin datos)
   */
  isColumnEmpty(columnKey: string): boolean {
    return this.emptyOptionalColumns.has(columnKey);
  }

  /**
   * Verifica si una columna está visible
   */
  isColumnVisible(columnKey: string): boolean {
    return this.columnVisibilityMap.get(columnKey) === true;
  }

  /**
   * TrackBy function para *ngFor de columnas
   */
  trackByColumnKey = (index: number, column: ImportColumnConfig): string => {
    return column.key;
  };
}
